/**
 * Tenant resolver — determines the current tenant from multiple sources.
 * Priority:
 *   1. Super-admin impersonation flag in localStorage
 *   2. Subdomain: dairyfresh.grocery.localappstore.in → "dairyfresh"
 *   3. Path slug: /t/dairyfresh → "dairyfresh" (fallback for dev)
 *   4. ?tenant=dairyfresh query param
 *   5. Cached slug in localStorage
 *   6. REACT_APP_DEFAULT_TENANT_SLUG build-time fallback
 *
 * A tenant's own custom domain (e.g. arfreshmart.in / www.arfreshmart.in)
 * matches none of the above synchronously — it's resolved once via an async
 * backend lookup (see resolveCustomDomainSlug) and then cached, so every
 * later call above hits step 5 (or step 2, if it's a subdomain).
 */

const PLATFORM_HOST = process.env.REACT_APP_PLATFORM_HOST || '';
const DEFAULT_SLUG = process.env.REACT_APP_DEFAULT_TENANT_SLUG || '';
const STORAGE_KEY = 'tenant_slug';

/**
 * Check if current hostname is a tenant subdomain.
 * e.g. dairyfresh.grocery.localappstore.in → "dairyfresh"
 * grocery.localappstore.in → null (it's the platform root)
 */
export function getSubdomainSlug() {
  if (!PLATFORM_HOST) return null;
  const hostname = window.location.hostname;
  if (hostname === PLATFORM_HOST || hostname === `www.${PLATFORM_HOST}`) return null;
  if (hostname.endsWith(`.${PLATFORM_HOST}`)) {
    const sub = hostname.replace(`.${PLATFORM_HOST}`, '');
    if (sub && sub !== 'www') return sub;
  }
  return null;
}

/**
 * Check if we're on the platform root (super admin / default).
 */
export function isPlatformRoot() {
  if (!PLATFORM_HOST) return true;
  const hostname = window.location.hostname;
  return hostname === PLATFORM_HOST || hostname === `www.${PLATFORM_HOST}` || !hostname.endsWith(`.${PLATFORM_HOST}`);
}

/**
 * Check if the current hostname could be a tenant's own custom domain —
 * i.e. it's neither the platform host nor a subdomain of it, and we're not
 * inside a native app webview (those resolve via ?tenant=/path instead).
 */
export function isCustomDomainCandidate() {
  if (!PLATFORM_HOST) return false;
  if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) return false;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
  if (hostname === PLATFORM_HOST || hostname === `www.${PLATFORM_HOST}`) return false;
  if (hostname.endsWith(`.${PLATFORM_HOST}`)) return false;
  return true;
}

/**
 * Resolve a tenant slug from a custom domain (e.g. arfreshmart.in or
 * www.arfreshmart.in) by asking the backend, which matches a tenant's
 * custom_domain setting regardless of a www. prefix on either side. Only
 * makes the network call when none of resolveTenantSlug()'s synchronous
 * sources (impersonation, subdomain, path, query param) already apply.
 */
export async function resolveCustomDomainSlug() {
  if (!isCustomDomainCandidate()) return null;
  if (resolveRoutedTenantSlug()) return null;

  try {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const res = await fetch(`${API}/store/by-domain/${window.location.hostname}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.slug) {
      cacheTenantSlug(data.slug);
      return data.slug;
    }
  } catch {
    /* network error — fall through to cache/default */
  }
  return null;
}

/**
 * Steps 1-5 only — no build-time default fallback. Returns null when the
 * tenant can't be determined synchronously, which is the signal a caller
 * (e.g. the custom-domain resolver, or a startup gate) needs to know
 * whether it's safe to fall back to DEFAULT_SLUG or whether an async
 * custom-domain lookup should happen first.
 */
export function resolveRoutedTenantSlug() {
  // 1. Super-admin impersonation
  const impersonateSlug = localStorage.getItem('impersonate_tenant_slug');
  if (impersonateSlug) return impersonateSlug;

  // 2. Subdomain
  const subSlug = getSubdomainSlug();
  if (subSlug) {
    cacheTenantSlug(subSlug);
    return subSlug;
  }

  // 3. Path slug: /t/dairyfresh (fallback for dev/preview)
  const pathMatch = window.location.pathname.match(/^\/t\/([a-z0-9-]+)/);
  if (pathMatch) {
    cacheTenantSlug(pathMatch[1]);
    return pathMatch[1];
  }

  // 4. Query param: ?tenant=dairyfresh
  const params = new URLSearchParams(window.location.search);
  const qSlug = params.get('tenant');
  if (qSlug) {
    // In native apps using fallback ?tenant= mode, the query param is the
    // source of truth. If a cached slug from a prior tenant exists, override it.
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached && cached !== qSlug) {
      console.log(`[Tenant] Overriding stale cached slug "${cached}" with URL slug "${qSlug}"`);
    }
    cacheTenantSlug(qSlug);
    return qSlug;
  }

  // 5. Cached slug
  const cached = localStorage.getItem(STORAGE_KEY);
  if (cached) return cached;

  return null;
}

export function resolveTenantSlug() {
  return resolveRoutedTenantSlug() || DEFAULT_SLUG;
}

export function cacheTenantSlug(slug) {
  if (slug) localStorage.setItem(STORAGE_KEY, slug);
}

export function clearTenantSlug() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('impersonate_tenant_slug');
}

/**
 * Build the canonical tenant URL (subdomain format).
 * Returns: https://dairyfresh.grocery.localappstore.in
 * Falls back to /t/slug if PLATFORM_HOST is not set.
 */
export function getTenantUrl(slug) {
  if (PLATFORM_HOST) {
    const protocol = window.location.protocol;
    return `${protocol}//${slug}.${PLATFORM_HOST}`;
  }
  return `/t/${slug}`;
}

/**
 * Build a display-friendly tenant domain.
 * Returns: dairyfresh.grocery.localappstore.in
 */
export function getTenantDomain(slug) {
  if (PLATFORM_HOST) return `${slug}.${PLATFORM_HOST}`;
  return `/t/${slug}`;
}

/**
 * Stale-tenant self-heal: if tenant 404s, clear cache and reload to default
 */
export function handleStaleTenant() {
  const guard = sessionStorage.getItem('tenant_heal_guard');
  if (guard) return;
  sessionStorage.setItem('tenant_heal_guard', '1');
  clearTenantSlug();
  window.location.href = PLATFORM_HOST ? `${window.location.protocol}//${PLATFORM_HOST}` : '/';
}
